import { useNodeId } from "@xyflow/react";
import { useCallback, Suspense, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  interfacesPagesAtomFamily_SUSPENDS,
  interfacePageAtomFamily_SUSPENDS,
  takeSnapshotSetter,
  closeSecondaryToolbarMenuSetter,
  formsTerminologyAtom,
} from "@zapier/canvas-state";
import { useFocusInput } from "../../../hooks/useFocusInput";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import { ErrorBoundary } from "../../ErrorBoundary";
import { SelectedItem } from "../SelectedItem";
import styles from "./FloatingPagesTypeahead.module.css";

type Props = {
  interfaceProjectId: string;
  interfacePageId: string | undefined;
};

function PagesTypeaheadLoader() {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label={`Search ${interfacesTerms.singularLower} pages`}
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <TypeaheadItem key="loading" id="loading">
          Loading pages...
        </TypeaheadItem>
      </Typeahead>
    </div>
  );
}

function SelectedPageSuspended({ interfaceProjectId, interfacePageId }: Props) {
  const nodeId = useNodeId()!;
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const selectedPage = useAtomValue(
    interfacePageAtomFamily_SUSPENDS({
      interfaceProjectId,
      interfacePageId,
      nodeId,
    }),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        pageId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedPage?.name || "Page not found"}
      icon={<Icon name="navInterfaces" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function PagesTypeaheadSuspended({
  interfaceProjectId,
}: Omit<Props, "interfacePageId">) {
  const interfacesTerms = useAtomValue(formsTerminologyAtom);
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  const { emitEventOnInterfaceConnect } = useEmitResourceConnectEvents();
  const pages = useAtomValue(
    interfacesPagesAtomFamily_SUSPENDS(interfaceProjectId),
  );
  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          pageId: key,
        },
      });
      emitEventOnInterfaceConnect(interfaceProjectId);
      closeSecondaryMenu();
    },
    [
      closeSecondaryMenu,
      emitEventOnInterfaceConnect,
      interfaceProjectId,
      takeSnapshot,
      updateNodeAndSave,
    ],
  );

  const PLACEHOLDER = `Search ${interfacesTerms.singularLower} pages (required)`;

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label={PLACEHOLDER}
        placeholder={PLACEHOLDER}
        ref={inputRef}
        onChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
        menuTrigger="focus" // To encourage user to choose a page
      >
        {pages.map((page) => {
          return (
            <TypeaheadItem
              key={page.id}
              id={page.id}
              textValue={page.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navInterfaces" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{page.name}</Text>
              </div>
            </TypeaheadItem>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function FloatingPagesTypeahead({
  interfaceProjectId,
  interfacePageId,
}: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PagesTypeaheadLoader />}>
        {interfacePageId ? (
          <SelectedPageSuspended
            interfaceProjectId={interfaceProjectId}
            interfacePageId={interfacePageId}
          />
        ) : (
          <PagesTypeaheadSuspended interfaceProjectId={interfaceProjectId} />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}
